import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { insightsApi } from '../api/insights';
import { Card } from '../components/Card';
import { Pill } from '../components/Pill';
import { DataTable, type Column } from '../components/DataTable';
import { fmtRelative } from '../lib/format';
import { ScanRevealOverlay } from './scan/ScanRevealOverlay';
import type { ScanCaptureConfig, ScanSessionRow } from '../api/types';

const OUTCOMES = ['', 'matched', 'no_match', 'corrected', 'abandoned'] as const;

function outcomeTone(o: string): 'good' | 'bad' | 'warn' | 'neutral' {
  if (o === 'matched') return 'good';
  if (o === 'no_match') return 'bad';
  if (o === 'corrected') return 'warn';
  return 'neutral';
}

export function ScanSessionsScreen() {
  const qc = useQueryClient();
  const [outcome, setOutcome] = useState('');

  const config = useQuery({ queryKey: ['scan-config'], queryFn: () => insightsApi.scanSessionConfig() });
  const sessions = useQuery({
    queryKey: ['scan-sessions', outcome || 'all'],
    queryFn: () => insightsApi.scanSessions({ outcome: outcome || undefined, limit: 200 }),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <ConfigPanel config={config.data} onSaved={() => qc.invalidateQueries({ queryKey: ['scan-config'] })} />

      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <h3 style={{ margin: 0, flex: 1 }}>Sessions</h3>
          <select value={outcome} onChange={(e) => setOutcome(e.target.value)} style={selectStyle}>
            {OUTCOMES.map((o) => (
              <option key={o || 'all'} value={o}>{o === '' ? 'All outcomes' : o}</option>
            ))}
          </select>
        </div>
        <DataTable<ScanSessionRow>
          rows={sessions.data?.items ?? []}
          loading={sessions.isLoading}
          rowKey={(r) => r.id}
          renderExpanded={(r) => <SessionDetail id={r.id} />}
          emptyState="No sessions yet — capture is off, or none uploaded."
          columns={sessionColumns}
        />
      </Card>
    </div>
  );
}

const sessionColumns: Column<ScanSessionRow>[] = [
  { key: 'createdAt', header: 'When', sortKey: 'createdAt', render: (r) => fmtRelative(r.createdAt) },
  { key: 'outcome', header: 'Outcome', render: (r) => <Pill tone={outcomeTone(r.outcome)}>{r.outcome}</Pill> },
  { key: 'lang', header: 'Lang', render: (r) => r.language ?? '—' },
  { key: 'dur', header: 'Duration', align: 'right', render: (r) => (r.durationSec != null ? `${r.durationSec.toFixed(1)}s` : '—') },
  { key: 'frames', header: 'Frames', align: 'right', render: (r) => r.framesProcessed ?? '—' },
  { key: 'video', header: 'Video', render: (r) => (r.videoUrl ? <Pill tone="brand">clip</Pill> : <span style={{ opacity: 0.5 }}>—</span>) },
  { key: 'ver', header: 'Version', hideMobile: true, render: (r) => r.appVersion ?? '—' },
  { key: 'device', header: 'Device', hideMobile: true, render: (r) => (r.deviceId ? r.deviceId.slice(0, 8) : '—') },
];

function ConfigPanel({ config, onSaved }: { config?: ScanCaptureConfig; onSaved: () => void }) {
  const [draft, setDraft] = useState<ScanCaptureConfig | null>(null);
  useEffect(() => { if (config) setDraft(config); }, [config]);

  const save = useMutation({
    mutationFn: (patch: Partial<ScanCaptureConfig>) => insightsApi.updateScanSessionConfig(patch),
    onSuccess: () => onSaved(),
  });

  if (!draft) return <Card>Loading config…</Card>;
  const on = !draft.killSwitch; // "on" = capture enabled = killSwitch false

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <h3 style={{ margin: 0, flex: 1 }}>Capture control</h3>
        <Pill tone={on ? 'good' : 'neutral'}>{on ? 'ON — capturing' : 'OFF — dark'}</Pill>
      </div>
      <p style={{ margin: '0 0 14px', color: 'var(--ink-2)', fontSize: 13 }}>
        Master switch + how many sessions upload their video. Changes apply to all apps on
        next launch — no app update needed.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-end' }}>
        <button
          onClick={() => save.mutate({ killSwitch: on })}
          disabled={save.isPending}
          style={{ ...btnStyle, background: on ? 'var(--bad)' : 'var(--good)' }}
        >
          {on ? 'Turn capture OFF' : 'Turn capture ON'}
        </button>

        <label style={fieldStyle}>
          <span style={labelStyle}>Video upload rate ({Math.round(draft.videoUploadRate * 100)}%)</span>
          <input
            type="range" min={0} max={1} step={0.05}
            value={draft.videoUploadRate}
            onChange={(e) => setDraft({ ...draft, videoUploadRate: Number(e.target.value) })}
            onMouseUp={() => save.mutate({ videoUploadRate: draft.videoUploadRate })}
          />
        </label>

        <label style={{ ...fieldStyle, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox" checked={draft.wifiOnly}
            onChange={(e) => { const v = e.target.checked; setDraft({ ...draft, wifiOnly: v }); save.mutate({ wifiOnly: v }); }}
          />
          <span style={labelStyle}>Wi-Fi only</span>
        </label>

        <div style={fieldStyle}>
          <span style={labelStyle}>Max clip</span>
          <span>{draft.maxSeconds}s</span>
        </div>
      </div>
      {save.isError && <p style={{ color: 'var(--bad)', fontSize: 12, marginTop: 10 }}>Save failed.</p>}
    </Card>
  );
}

function SessionDetail({ id }: { id: number }) {
  const detail = useQuery({ queryKey: ['scan-session', id], queryFn: () => insightsApi.scanSession(id) });
  const d = detail.data;
  const videoRef = useRef<HTMLVideoElement | null>(null);

  return (
    <div>
      {detail.isLoading && <p style={{ color: 'var(--ink-2)' }}>Loading…</p>}
      {d && (
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ flex: '0 0 380px', maxWidth: '100%' }}>
            {d.videoUrl ? (
              <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', background: '#000' }}>
                {/* Prefer the processed clip (card + price baked into the pixels →
                    survives fullscreen/download). Fall back to the raw clip with a
                    live DOM overlay until the processor has run (≤ ~5 min). */}
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video
                  key={d.processedVideoUrl ?? d.videoUrl}
                  ref={videoRef}
                  src={d.processedVideoUrl ?? d.videoUrl}
                  controls
                  playsInline
                  style={{ display: 'block', width: '100%', background: '#000' }}
                />
                {!d.processedVideoUrl && <ScanRevealOverlay telemetry={d.telemetry} videoRef={videoRef} />}
              </div>
            ) : (
              <div style={{ padding: 20, color: 'var(--ink-2)', border: '1px dashed var(--line)', borderRadius: 12 }}>
                No video for this session.
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10, fontSize: 12, color: 'var(--ink-2)' }}>
              {d.videoUrl && (
                <Pill tone={d.processedVideoUrl ? 'good' : 'neutral'}>
                  {d.processedVideoUrl ? 'processed' : 'raw · live overlay'}
                </Pill>
              )}
              <Pill>{d.language ?? '—'}</Pill>
              <Pill>{d.durationSec != null ? `${d.durationSec.toFixed(1)}s` : '—'}</Pill>
              <Pill>{d.framesProcessed ?? 0} frames</Pill>
              {d.torchUsed && <Pill tone="warn">torch</Pill>}
              {d.thermalState && <Pill tone={d.thermalState === 'nominal' ? 'neutral' : 'warn'}>{d.thermalState}</Pill>}
              {d.appVersion && <Pill>v{d.appVersion}</Pill>}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 260, maxHeight: 420, overflowY: 'auto' }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--ink-2)' }}>Decision timeline</div>
            {(d.telemetry ?? []).length === 0 && <p style={{ color: 'var(--ink-2)' }}>No events.</p>}
            {(d.telemetry ?? []).map((e, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, padding: '3px 0', borderBottom: '1px solid var(--line)', fontSize: 12 }}>
                <span style={{ opacity: 0.6, width: 56 }}>{(e.tMs / 1000).toFixed(1)}s</span>
                <span style={{ width: 64, fontWeight: 600 }}>{e.kind}</span>
                <span style={{ flex: 1, color: 'var(--ink-2)' }}>
                  {e.quad != null ? `card:${e.quad ? '✓' : '·'} ` : ''}
                  {e.texture != null ? `tex:${e.texture.toFixed(2)} ` : ''}
                  {e.note ?? ''}{e.cardId ? ` ${e.cardId}` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const selectStyle: React.CSSProperties = {
  background: 'var(--bg-sunken)', color: 'var(--ink-1)', border: '1px solid var(--line)',
  borderRadius: 8, padding: '6px 10px', fontSize: 13,
};
const btnStyle: React.CSSProperties = {
  border: 'none', borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 600,
  color: '#fff', cursor: 'pointer',
};
const fieldStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 };
const labelStyle: React.CSSProperties = { fontSize: 12, color: 'var(--ink-2)' };
